import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ConfirmDialog.css';

/**
 * "Are you sure?" - the one dialog for it, in place of window.confirm() and
 * window.prompt(), which a phone shows as a system box that cannot be styled,
 * themed or validated, and which some in-app browsers refuse to show at all.
 *
 * Promoted from LinksPage, which had the first one. What it has to get right:
 *
 *   ESCAPE AND THE BACKDROP CANCEL IT. The Escape listener runs in the
 *   capture phase and stops the key there, so a dialog underneath (a Modal,
 *   a to-do sheet) does not close with it.
 *
 *   FOCUS MOVES IN - to the text field when there is one, else to the
 *   confirming button, so Enter answers it as it answered the browser's box -
 *   stays in (Tab wraps), and goes back where it came from on close.
 *
 *   role="alertdialog" with aria-modal, a labelling title and a describing
 *   message, so a screen reader says what is being asked.
 *
 *   ON A PHONE IT IS A SHEET along the foot of the screen, full width, with
 *   the buttons stacked; see ConfirmDialog.css.
 *
 * Props: open, title, message (a string or nodes; children work too),
 * confirmLabel, cancelLabel, danger (a red confirming button), onConfirm,
 * onCancel, and an optional prompt { label, placeholder, required, initial,
 * multiline } that adds a text field whose value is handed to onConfirm.
 * onConfirm may return a promise: the buttons are held while it runs.
 *
 * Most screens want neither the props nor the state: useConfirm() below
 * gives them a one-liner.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
let seq = 0;

export default function ConfirmDialog({
  open = true, title, message = null, children = null, confirmLabel = 'OK', cancelLabel = 'Cancel',
  danger = false, prompt = null, onConfirm, onCancel
}) {
  const [value, setValue] = useState(() => (prompt && prompt.initial) || '');
  const [busy, setBusy] = useState(false);
  const panel = useRef(null);
  const field = useRef(null);
  const returnTo = useRef(null);
  const alive = useRef(false);
  const backdropPress = useRef(false);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const idRef = useRef(null);
  if (!idRef.current) { seq += 1; idRef.current = `cfd-${seq}`; }
  const id = idRef.current;

  useEffect(() => {
    if (!open) return undefined;
    alive.current = true;
    setBusy(false);
    setValue((prompt && prompt.initial) || '');
    returnTo.current = document.activeElement;
    const el = panel.current;
    const first = (field.current || (el && el.querySelector('[data-cfd-confirm]')) || el);
    if (first) first.focus({ preventScroll: true });

    // Capture, so a dialog underneath (Modal, the to-do sheet) never sees
    // the Escape that closed this one.
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (cancelRef.current) cancelRef.current();
    };
    document.addEventListener('keydown', onKey, true);
    const body = document.body;
    const was = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      alive.current = false;
      document.removeEventListener('keydown', onKey, true);
      body.style.overflow = was;
      const back = returnTo.current;
      if (back && typeof back.focus === 'function' && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch { /* gone */ }
      }
    };
    // `prompt` is read once, when it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const required = Boolean(prompt && prompt.required);
  const empty = required && !value.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (empty) { if (field.current) field.current.focus(); return; }
    setBusy(true);
    try {
      await onConfirm(prompt ? value : undefined);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  // Every key stays in here: the screen behind has shortcuts of its own
  // (the to-do list acts on Delete and single letters) that must not fire
  // while a question is up. Tab wraps round inside the dialog.
  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key !== 'Tab' || !panel.current) return;
    const els = [...panel.current.querySelectorAll(FOCUSABLE)].filter((x) => x.offsetParent !== null);
    if (!els.length) return;
    const first = els[0];
    const last = els[els.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  if (!open) return null;
  const hasBody = message !== null && message !== undefined && message !== '' ? true : Boolean(children);
  const Field = prompt && prompt.multiline ? 'textarea' : 'input';

  return (
    <div
      className="cfd-backdrop"
      onKeyDown={onKeyDown}
      onMouseDown={(e) => { backdropPress.current = e.target === e.currentTarget; }}
      onMouseUp={(e) => {
        if (backdropPress.current && e.target === e.currentTarget && !busy && onCancel) onCancel();
        backdropPress.current = false;
      }}
    >
      <form
        className={`cfd-panel${danger ? ' is-danger' : ''}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={hasBody ? `${id}-body` : undefined}
        tabIndex={-1}
        ref={panel}
        onSubmit={submit}
      >
        <h2 className="cfd-title" id={`${id}-title`}>{title}</h2>
        {hasBody && (
          <div className="cfd-body" id={`${id}-body`}>
            {typeof message === 'string' ? <p>{message}</p> : message}
            {children}
          </div>
        )}
        {prompt && (
          <label className="cfd-field">
            <span className="cfd-field-label">{prompt.label}{required && <span className="cfd-required" aria-hidden="true"> *</span>}</span>
            <Field
              ref={field}
              className="cfd-input"
              type={prompt.multiline ? undefined : 'text'}
              rows={prompt.multiline ? 3 : undefined}
              value={value}
              placeholder={prompt.placeholder || ''}
              required={required}
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
            />
          </label>
        )}
        <div className="cfd-actions">
          <button type="button" className="btn cfd-cancel" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button type="submit" className={`btn ${danger ? 'btn-danger' : 'btn-primary'} cfd-confirm`}
                  disabled={busy || empty} data-cfd-confirm="">
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * A screen's one-liner:
 *
 *   const [confirm, confirmDialog] = useConfirm();
 *   ...
 *   if (!(await confirm({ title: 'Delete this comment?', confirmLabel: 'Delete', danger: true }))) return;
 *   ...
 *   return (<div>…{confirmDialog}</div>);
 *
 * confirm(opts) takes the dialog's props (or a string, used as the title) and
 * resolves true or false; with a `prompt` it resolves the text typed (an empty
 * string when none was and the field was not required) or false when it was
 * cancelled. A second question asked while one is still up cancels the first.
 */
export function useConfirm() {
  const [ask, setAsk] = useState(null);
  const current = useRef(null);
  const confirm = useCallback((opts) => new Promise((resolve) => {
    if (current.current) current.current.resolve(false);
    const next = { opts: typeof opts === 'string' ? { title: opts } : (opts || {}), resolve };
    current.current = next;
    setAsk(next);
  }), []);
  const settle = (what) => {
    const a = current.current;
    current.current = null;
    setAsk(null);
    if (a) a.resolve(what);
  };
  const element = ask ? (
    <ConfirmDialog
      open
      {...ask.opts}
      onConfirm={(v) => settle(ask.opts.prompt ? String(v === undefined || v === null ? '' : v) : true)}
      onCancel={() => settle(false)}
    />
  ) : null;
  return [confirm, element];
}
